/**
 * Admin Tenants
 * View and manage all tenants/firms
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Users, Briefcase, FileText, Search,
  Loader2, ExternalLink, ChevronRight
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

interface Tenant {
  id: string;
  name: string;
  domain?: string;
  created_at: string;
  plan: string;
  status: string;
  user_count: number;
  case_count: number;
  document_count: number;
  logo_url?: string;
}

const AdminTenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  
  useEffect(() => {
    fetchTenants();
  }, []);
  
  const fetchTenants = async (searchQuery?: string) => {
    try {
      const token = auth.getToken();
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`${API}/api/admin/tenants${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.tenants);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTenantDetail = async (tenantId: string) => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/api/admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTenant(response.data);
    } catch (error) {
      console.error('Failed to fetch tenant detail:', error);
    }
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    fetchTenants(search);
  };
  
  const getPlanBadge = (plan: string) => {
    const styles: Record<string, string> = {
      FREE: 'bg-slate-700 text-slate-300',
      STARTER: 'bg-blue-500/20 text-blue-400',
      PROFESSIONAL: 'bg-violet-500/20 text-violet-400',
      ENTERPRISE: 'bg-amber-500/20 text-amber-400'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${styles[plan] || styles.FREE}`}>
        {plan}
      </span>
    );
  };
  
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Firmaer</h1>
        <p className="text-slate-400 mt-1">Alle registrerte advokatfirmaer</p>
      </div>
      
      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk etter firma..."
            className="pl-10 bg-slate-900 border-slate-800 text-white placeholder-slate-500"
          />
        </div>
      </form>
      
      <div className="flex gap-6">
        {/* Tenants list */}
        <div className={`flex-1 ${selectedTenant ? 'max-w-md' : ''}`}>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-12 bg-slate-900 border border-slate-800 rounded-2xl">
              <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400">Ingen firmaer funnet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tenants.map((tenant, index) => (
                <motion.div
                  key={tenant.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => fetchTenantDetail(tenant.id)}
                  className={`bg-slate-900 border rounded-xl p-4 cursor-pointer transition-all ${
                    selectedTenant?.tenant?.id === tenant.id
                      ? 'border-violet-500'
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {tenant.logo_url ? (
                        <img src={tenant.logo_url} alt={tenant.name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{tenant.name}</p>
                          {getPlanBadge(tenant.plan)}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {tenant.user_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {tenant.case_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {tenant.document_count}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
        
        {/* Tenant detail panel */}
        {selectedTenant && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-4 mb-6">
              {selectedTenant.tenant.logo_url ? (
                <img src={selectedTenant.tenant.logo_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 bg-slate-800 rounded-xl flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-slate-400" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-white">{selectedTenant.tenant.name}</h2>
                <p className="text-slate-400 text-sm">
                  Opprettet {format(new Date(selectedTenant.tenant.created_at), 'd. MMM yyyy', { locale: nb })}
                </p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{selectedTenant.stats.user_count}</p>
                <p className="text-sm text-slate-400">Brukere</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{selectedTenant.stats.case_count}</p>
                <p className="text-sm text-slate-400">Saker</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{selectedTenant.stats.document_count}</p>
                <p className="text-sm text-slate-400">Dokumenter</p>
              </div>
            </div>
            
            {/* Users */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">Brukere</h3>
              <div className="space-y-2">
                {selectedTenant.users.slice(0, 5).map((user: any) => (
                  <div key={user.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/50">
                    <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white text-sm">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm text-white">{user.name || 'Unnamed'}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminTenants;
